/* tslint:disable */
/* eslint-disable */
/**
 * Internal Variable Definitions Administration API
 *  ## Introduction  Variable Definitions are centralized definitions of concrete variables which are typically present in multiple datasets. Variable Definitions support standardization of data and metadata and facilitate sharing and joining of data by clarifying when variables have an identical definition.  ## Maintenance of Variable Definitions This API allows for creation, maintenance and access of Variable Definitions.  ### Ownership Creation and maintenance of variables may only be performed by Statistics Norway employees representing a specific Dapla team, who are defined as the owners of a given Variable Definition. In order to create variables, the single team an owner currently represents must be specified in the bearer token. This is currently only supported from the Dapla Lab platform. All maintenance is to be performed by the owners, with no intervention from administrators.  ### Status All Variable Definitions have an associated status. The possible values for status are `DRAFT`, `PUBLISHED_INTERNAL` and `PUBLISHED_EXTERNAL`.  #### Draft When a Variable Definition is created it is assigned the status `DRAFT`. Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Mutable (it may be changed directly without need for versioning). - Not suitable to refer to from other systems.  This status may be changed to `PUBLISHED_INTERNAL` or `PUBLISHED_EXTERNAL` with a direct update.  #### Published Internal Under this status the Variable Definition is:  - Only visible to Statistics Norway employees. - Immutable (all changes are versioned). - Suitable to refer to in internal systems for statistics production. - Not suitable to refer to for external use (for example in Statistikkbanken).  This status may be changed to `PUBLISHED_EXTERNAL` by creating a Patch version.  #### Published External Under this status the Variable Definition is:  - Visible to the general public. - Immutable (all changes are versioned). - Suitable to refer to from any system.  This status may not be changed as it would break immutability. If a Variable Definition is no longer relevant then its period of validity should be ended by specifying a `valid_until` date in a Patch version.  ### Immutability Variable Definitions are immutable. This means that any changes must be performed in a strict versioning system. Consumers can avoid being exposed to breaking changes by specifying a `date_of_validity` when they request a Variable Definition.  #### Patches Patches are for changes which do not affect the fundamental meaning of the Variable Definition.  #### Validity Periods Validity Periods are versions with a period defined by a `valid_from` date and optionally a `valid_until` date. If the fundamental meaning of a Variable Definition is to be changed, it should be done by creating a new Validity Period.  
 *
 * The version of the OpenAPI document: 0.1
 * Contact: metadata@ssb.no
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { LanguageStringType } from './LanguageStringType';
import {
    LanguageStringTypeFromJSON,
    LanguageStringTypeFromJSONTyped,
    LanguageStringTypeToJSON,
    LanguageStringTypeToJSONTyped,
} from './LanguageStringType';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * Create a new Validity Period on a Published Variable Definition.
 * @export
 * @interface CreateValidityPeriod
 */
export interface CreateValidityPeriod {
    /**
     * 
     * @type {LanguageStringType}
     * @memberof CreateValidityPeriod
     */
    name?: LanguageStringType | null;
    /**
     * 
     * @type {LanguageStringType}
     * @memberof CreateValidityPeriod
     */
    definition: LanguageStringType;
    /**
     * ID of a classification or code list from Klass. The given classification defines all possible values for the defined variable.
     * @type {string}
     * @memberof CreateValidityPeriod
     */
    classification_reference?: string | null;
    /**
     * A list of one or more unit types, e.g. person, vehicle, household. Must be defined as codes from https://dataportal.ssb.no/classifications/702.
     * @type {Array<string>}
     * @memberof CreateValidityPeriod
     */
    unit_types?: Array<string> | null;
    /**
     * A list of subject fields that the variable is used in. Must be defined as codes from https://dataportal.ssb.no/classifications/618.
     * @type {Array<string>}
     * @memberof CreateValidityPeriod
     */
    subject_fields?: Array<string> | null;
    /**
     * True if variable instances contain particularly sensitive information. Applies even if the information or identifiers are pseudonymized. Information within the following categories are regarded as particularly sensitive: Ethnicity, Political alignment, Religion, Philosophical beliefs, Union membership, Genetics, Biometrics, Health, Sexual relations, Sexual orientation
     * @type {boolean}
     * @memberof CreateValidityPeriod
     */
    contains_special_categories_of_personal_data?: boolean | null;
    /**
     * Type of measurement for the variable, e.g. length, volume, currency. Must be defined as codes from https://dataportal.ssb.no/classifications/303
     * @type {string}
     * @memberof CreateValidityPeriod
     */
    measurement_type?: string | null;
    /**
     * The variable definition is valid from this date inclusive
     * @type {Date}
     * @memberof CreateValidityPeriod
     */
    valid_from: Date;
    /**
     * A link (URI) to an external definition/documentation
     * @type {string}
     * @memberof CreateValidityPeriod
     */
    external_reference_uri?: string | null;
    /**
     * 
     * @type {LanguageStringType}
     * @memberof CreateValidityPeriod
     */
    comment?: LanguageStringType | null;
    /**
     * Link(s) to related definitions of variables - a list of one or more definitions. For example for a variable after-tax income it could be relevant to link to definitions of income from work, property income etc.
     * @type {Array<string>}
     * @memberof CreateValidityPeriod
     */
    related_variable_definition_uris?: Array<string> | null;
    /**
     * 
     * @type {Contact}
     * @memberof CreateValidityPeriod
     */
    contact?: Contact | null;
}

/**
 * Check if a given object implements the CreateValidityPeriod interface.
 */
export function instanceOfCreateValidityPeriod(value: object): value is CreateValidityPeriod {
    if (!('definition' in value) || value['definition'] === undefined) return false;
    if (!('valid_from' in value) || value['valid_from'] === undefined) return false;
    return true;
}

export function CreateValidityPeriodFromJSON(json: any): CreateValidityPeriod {
    return CreateValidityPeriodFromJSONTyped(json, false);
}

export function CreateValidityPeriodFromJSONTyped(json: any, ignoreDiscriminator: boolean): CreateValidityPeriod {
    if (json == null) {
        return json;
    }
    return {
        
        'name': json['name'] == null ? undefined : LanguageStringTypeFromJSON(json['name']),
        'definition': LanguageStringTypeFromJSON(json['definition']),
        'classification_reference': json['classification_reference'] == null ? undefined : json['classification_reference'],
        'unit_types': json['unit_types'] == null ? undefined : json['unit_types'],
        'subject_fields': json['subject_fields'] == null ? undefined : json['subject_fields'],
        'contains_special_categories_of_personal_data': json['contains_special_categories_of_personal_data'] == null ? undefined : json['contains_special_categories_of_personal_data'],
        'measurement_type': json['measurement_type'] == null ? undefined : json['measurement_type'],
        'valid_from': (new Date(json['valid_from'])),
        'external_reference_uri': json['external_reference_uri'] == null ? undefined : json['external_reference_uri'],
        'comment': json['comment'] == null ? undefined : LanguageStringTypeFromJSON(json['comment']),
        'related_variable_definition_uris': json['related_variable_definition_uris'] == null ? undefined : json['related_variable_definition_uris'],
        'contact': json['contact'] == null ? undefined : ContactFromJSON(json['contact']),
    };
}

export function CreateValidityPeriodToJSON(json: any): CreateValidityPeriod {
    return CreateValidityPeriodToJSONTyped(json, false);
}

export function CreateValidityPeriodToJSONTyped(value?: CreateValidityPeriod | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'name': LanguageStringTypeToJSON(value['name']),
        'definition': LanguageStringTypeToJSON(value['definition']),
        'classification_reference': value['classification_reference'],
        'unit_types': value['unit_types'],
        'subject_fields': value['subject_fields'],
        'contains_special_categories_of_personal_data': value['contains_special_categories_of_personal_data'],
        'measurement_type': value['measurement_type'],
        'valid_from': value['valid_from'].toISOString().substring(0,10),
        'external_reference_uri': value['external_reference_uri'],
        'comment': LanguageStringTypeToJSON(value['comment']),
        'related_variable_definition_uris': value['related_variable_definition_uris'],
        'contact': ContactToJSON(value['contact']),
    };
}

